/**
 * SessionEndedBanner (#470) — replaces the old dead-end SessionEndedNotice.
 *
 * Rendered in place of the composer when the loaded conversation is ended
 * ('completed' or 'abandoned'). The transcript stays visible above; the
 * banner offers to resume the SAME thread (primary) or start a new session
 * (secondary). The parent owns the resume call (POST
 * /api/conversations/:id/resume) — on success it clears the ended flag and
 * the live composer returns; on failure it passes `error` and the composer
 * stays hidden.
 */

import { Loader2 } from 'lucide-react';
import { Button } from '@core/ui/button';
import { useChatCopy } from '../../config';

export interface SessionEndedBannerProps {
  /** Resume the current thread — parent performs the POST /resume call. */
  onResume: () => void;
  /** Start a fresh conversation (existing new-conversation behavior). */
  onStartNew: () => void;
  /** True while the resume call is in flight; disables both actions. */
  resuming?: boolean;
  /** Inline error from a failed resume call (composer stays hidden). */
  error?: string | null;
  /**
   * [#1312 P1-2] False for voice transcripts opened in the chat viewer:
   * POST /resume 404s for voice labels by contract, so the resume CTA must
   * not render (it flipped the voice record to a permanently-active zombie
   * behind a composer whose every send 400s). Only "start new" remains.
   */
  resumable?: boolean;
}

export function SessionEndedBanner({
  onResume,
  onStartNew,
  resuming = false,
  error = null,
  resumable = true,
}: SessionEndedBannerProps) {
  // #2109 R3-B24: the banner's words arrive through the copy pack.
  const copy = useChatCopy().sessionEnded;
  return (
    <div
      role="status"
      className="flex flex-col items-center gap-3 rounded-editorial border border-chat-border bg-chat-surface px-5 py-4 text-center shadow-chat-md sm:flex-row sm:justify-between sm:text-left"
    >
      <div className="min-w-0">
        <p className="m-0 text-body-sm text-chat-text-secondary">
          {resumable ? copy.ended : copy.voiceViewOnly}
        </p>
        {error && (
          <p role="alert" className="m-0 mt-1 text-caption text-destructive">
            {error}
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
        <Button
          variant={resumable ? 'outline' : 'default'}
          disabled={resuming}
          onClick={onStartNew}
        >
          {copy.startNew}
        </Button>
        {resumable && (
          <Button disabled={resuming} onClick={onResume}>
            {resuming ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" />
                {copy.resuming}
              </>
            ) : (
              copy.resume
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
